import { useLanguage } from '@/i18n/LanguageContext'
import { useTheme } from '@/contexts/ThemeContext'
import { cn } from '@/lib/utils'
import { FileText, AlertTriangle } from 'lucide-react'

export function TermsPage() {
  const { language } = useLanguage()
  const { theme } = useTheme()

  const content = {
    es: {
      title: 'Términos y Condiciones',
      lastUpdate: 'Última actualización: Enero 2025',
      importantNotice: {
        title: 'Aviso Importante',
        content: `El cambio de W2USDT a USDT estará disponible ÚNICAMENTE cuando exista liquidez suficiente de USDT. W2USDT NO GARANTIZA el cambio en todo momento. Al utilizar este servicio, usted acepta y comprende esta limitación.`
      },
      sections: [
        {
          title: '1. Aceptación de los Términos',
          content: `Al acceder y utilizar el servicio de W2USDT Bridge, usted acepta estar sujeto a estos términos y condiciones. Si no está de acuerdo con alguna parte de estos términos, no debe utilizar el servicio.

El uso continuado del servicio después de cualquier modificación de estos términos constituye su aceptación de dichos cambios.`
        },
        {
          title: '2. Descripción del Servicio',
          content: `W2USDT Bridge es un servicio que permite a los usuarios convertir tokens W2USDT (desplegados en la red Uni2, Chain ID: 111888) a USDT en la red Binance Smart Chain (BSC, Chain ID: 56).

El servicio opera mediante contratos inteligentes y un sistema de procesamiento manual/automático de solicitudes.`
        },
        {
          title: '3. Disponibilidad y Liquidez',
          content: `IMPORTANTE: El servicio de conversión de W2USDT a USDT está sujeto a la disponibilidad de liquidez.

• El cambio de W2USDT a USDT SOLO se realizará cuando exista liquidez suficiente de USDT en el sistema.
• W2USDT NO GARANTIZA que el cambio esté disponible en todo momento.
• Las solicitudes de bridge pueden quedar pendientes hasta que haya liquidez disponible.
• W2USDT no se hace responsable por retrasos o imposibilidad de completar el cambio debido a falta de liquidez.
• Los usuarios deben entender y aceptar que pueden existir períodos donde el cambio no sea posible.`
        },
        {
          title: '4. Sin Garantías',
          content: `W2USDT Bridge se proporciona "TAL CUAL" y "SEGÚN DISPONIBILIDAD", sin garantías de ningún tipo, ya sean expresas o implícitas.

No garantizamos:
• Que el servicio esté disponible de forma ininterrumpida
• Que el cambio de W2USDT a USDT esté siempre disponible
• Tiempos específicos de procesamiento
• La ausencia de errores o fallos técnicos
• La compatibilidad con todas las wallets o dispositivos`
        },
        {
          title: '5. Proceso de Bridge',
          content: `El proceso de bridge funciona de la siguiente manera:

1. El usuario bloquea sus tokens W2USDT en el contrato de Uni2
2. La solicitud queda registrada y pendiente de procesamiento
3. Una vez verificada y aprobada, se envía USDT a la dirección BSC del usuario
4. El proceso se completa cuando hay confirmación en ambas blockchains

Las solicitudes pueden ser rechazadas o canceladas por motivos de seguridad o verificación.`
        },
        {
          title: '6. Comisiones',
          content: `El servicio puede aplicar comisiones por las operaciones de bridge. Las comisiones vigentes se muestran en la interfaz antes de confirmar cada operación.

Además de nuestras comisiones, los usuarios deben pagar las tarifas de gas de las blockchains involucradas (Uni2 y BSC).`
        },
        {
          title: '7. Responsabilidad del Usuario',
          content: `El usuario es responsable de:

• Proporcionar direcciones de wallet correctas
• Mantener la seguridad de sus claves privadas y wallets
• Verificar los detalles de la transacción antes de confirmar
• Entender los riesgos asociados con las criptomonedas y blockchain
• Cumplir con las leyes y regulaciones aplicables en su jurisdicción`
        },
        {
          title: '8. Limitación de Responsabilidad',
          content: `En la máxima medida permitida por la ley, W2USDT Bridge y sus operadores no serán responsables por:

• Pérdidas derivadas de la falta de liquidez para completar el cambio
• Retrasos en el procesamiento de solicitudes
• Errores del usuario al proporcionar direcciones incorrectas
• Pérdidas por hackeos o vulnerabilidades de las wallets del usuario
• Fluctuaciones en el valor de los tokens
• Problemas técnicos en las blockchains de Uni2 o BSC
• Daños indirectos, incidentales o consecuentes`
        },
        {
          title: '9. Cancelaciones y Reembolsos',
          content: `Los usuarios pueden cancelar solicitudes de bridge que estén en estado "Pendiente". Una vez que una solicitud ha sido aprobada o completada, no puede ser cancelada.

En caso de problemas técnicos que impidan completar una operación, los tokens bloqueados podrán ser devueltos según el procedimiento establecido.`
        },
        {
          title: '10. Modificaciones del Servicio',
          content: `Nos reservamos el derecho de:

• Modificar, suspender o discontinuar el servicio en cualquier momento
• Cambiar las comisiones aplicables
• Actualizar estos términos y condiciones
• Establecer límites de uso del servicio`
        },
        {
          title: '11. Jurisdicción',
          content: `Estos términos se rigen por las leyes aplicables. Cualquier disputa relacionada con el servicio se resolverá de acuerdo con los mecanismos de resolución de disputas apropiados.`
        },
        {
          title: '12. Contacto',
          content: `Para consultas relacionadas con estos términos y condiciones, puede contactarnos a través de los canales oficiales del proyecto W2USDT.`
        }
      ]
    },
    en: {
      title: 'Terms and Conditions',
      lastUpdate: 'Last updated: January 2025',
      importantNotice: {
        title: 'Important Notice',
        content: `The exchange from W2USDT to USDT will be available ONLY when there is sufficient USDT liquidity. W2USDT DOES NOT GUARANTEE the exchange at all times. By using this service, you accept and understand this limitation.`
      },
      sections: [
        {
          title: '1. Acceptance of Terms',
          content: `By accessing and using the W2USDT Bridge service, you agree to be bound by these terms and conditions. If you do not agree with any part of these terms, you should not use the service.

Continued use of the service after any modification of these terms constitutes your acceptance of such changes.`
        },
        {
          title: '2. Service Description',
          content: `W2USDT Bridge is a service that allows users to convert W2USDT tokens (deployed on the Uni2 network, Chain ID: 111888) to USDT on the Binance Smart Chain network (BSC, Chain ID: 56).

The service operates through smart contracts and a manual/automatic request processing system.`
        },
        {
          title: '3. Availability and Liquidity',
          content: `IMPORTANT: The W2USDT to USDT conversion service is subject to liquidity availability.

• The exchange from W2USDT to USDT will ONLY be performed when there is sufficient USDT liquidity in the system.
• W2USDT DOES NOT GUARANTEE that the exchange will be available at all times.
• Bridge requests may remain pending until liquidity is available.
• W2USDT is not responsible for delays or inability to complete the exchange due to lack of liquidity.
• Users must understand and accept that there may be periods where the exchange is not possible.`
        },
        {
          title: '4. No Warranties',
          content: `W2USDT Bridge is provided "AS IS" and "AS AVAILABLE", without warranties of any kind, whether express or implied.

We do not guarantee:
• That the service will be available uninterrupted
• That the W2USDT to USDT exchange will always be available
• Specific processing times
• The absence of errors or technical failures
• Compatibility with all wallets or devices`
        },
        {
          title: '5. Bridge Process',
          content: `The bridge process works as follows:

1. The user locks their W2USDT tokens in the Uni2 contract
2. The request is registered and pending processing
3. Once verified and approved, USDT is sent to the user's BSC address
4. The process is completed when there is confirmation on both blockchains

Requests may be rejected or cancelled for security or verification reasons.`
        },
        {
          title: '6. Fees',
          content: `The service may apply fees for bridge operations. Current fees are displayed in the interface before confirming each operation.

In addition to our fees, users must pay the gas fees of the blockchains involved (Uni2 and BSC).`
        },
        {
          title: '7. User Responsibility',
          content: `The user is responsible for:

• Providing correct wallet addresses
• Maintaining the security of their private keys and wallets
• Verifying transaction details before confirming
• Understanding the risks associated with cryptocurrencies and blockchain
• Complying with applicable laws and regulations in their jurisdiction`
        },
        {
          title: '8. Limitation of Liability',
          content: `To the maximum extent permitted by law, W2USDT Bridge and its operators shall not be liable for:

• Losses arising from lack of liquidity to complete the exchange
• Delays in request processing
• User errors in providing incorrect addresses
• Losses from hacks or vulnerabilities in user wallets
• Fluctuations in token value
• Technical problems on Uni2 or BSC blockchains
• Indirect, incidental, or consequential damages`
        },
        {
          title: '9. Cancellations and Refunds',
          content: `Users can cancel bridge requests that are in "Pending" status. Once a request has been approved or completed, it cannot be cancelled.

In case of technical problems that prevent completing an operation, locked tokens may be returned according to the established procedure.`
        },
        {
          title: '10. Service Modifications',
          content: `We reserve the right to:

• Modify, suspend, or discontinue the service at any time
• Change applicable fees
• Update these terms and conditions
• Establish service usage limits`
        },
        {
          title: '11. Jurisdiction',
          content: `These terms are governed by applicable laws. Any dispute related to the service will be resolved according to appropriate dispute resolution mechanisms.`
        },
        {
          title: '12. Contact',
          content: `For inquiries related to these terms and conditions, you can contact us through the official W2USDT project channels.`
        }
      ]
    }
  }

  const t = content[language]

  return (
    <div className="max-w-4xl mx-auto py-8">
      <div className={cn(
        'rounded-2xl border p-8 md:p-12',
        theme === 'dark'
          ? 'bg-dark-900/60 border-dark-800'
          : 'bg-white border-gray-200'
      )}>
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-uni-500 to-uni-600 flex items-center justify-center shadow-glow-sm">
            <FileText className="w-7 h-7 text-dark-950" />
          </div>
          <div>
            <h1 className={cn(
              'text-3xl font-display font-bold',
              theme === 'dark' ? 'text-white' : 'text-gray-900'
            )}>
              {t.title}
            </h1>
            <p className={cn('text-sm', theme === 'dark' ? 'text-dark-400' : 'text-gray-500')}>
              {t.lastUpdate}
            </p>
          </div>
        </div>

        {/* Important Notice */}
        <div className={cn(
          'rounded-xl border-2 p-6 mb-10',
          theme === 'dark'
            ? 'bg-yellow-500/10 border-yellow-500/30'
            : 'bg-yellow-50 border-yellow-300'
        )}>
          <div className="flex items-start gap-4">
            <div className="flex-shrink-0">
              <AlertTriangle className="w-8 h-8 text-yellow-500" />
            </div>
            <div>
              <h3 className={cn(
                'text-lg font-display font-bold mb-2',
                theme === 'dark' ? 'text-yellow-400' : 'text-yellow-700'
              )}>
                {t.importantNotice.title}
              </h3>
              <p className={cn(
                'font-medium',
                theme === 'dark' ? 'text-yellow-300/90' : 'text-yellow-800'
              )}>
                {t.importantNotice.content}
              </p>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="space-y-8">
          {t.sections.map((section, i) => (
            <div key={i}>
              <h2 className={cn(
                'text-xl font-display font-semibold mb-4',
                theme === 'dark' ? 'text-white' : 'text-gray-900'
              )}>
                {section.title}
              </h2>
              <div className={cn(
                'whitespace-pre-line leading-relaxed',
                theme === 'dark' ? 'text-dark-300' : 'text-gray-600'
              )}>
                {section.content}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}
